import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'tw:group/button tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-transparent tw:bg-clip-padding tw:text-sm tw:font-medium tw:whitespace-nowrap tw:transition-all tw:outline-none tw:select-none tw:focus-visible:border-ring tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:active:not-aria-[haspopup]:translate-y-px tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:aria-invalid:border-destructive tw:aria-invalid:ring-3 tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:border-destructive/50 tw:dark:aria-invalid:ring-destructive/40 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:hover:bg-primary/80',
        outline:
          'tw:border-border tw:bg-background tw:shadow-xs tw:hover:bg-muted tw:hover:text-foreground tw:aria-expanded:bg-muted tw:aria-expanded:text-foreground tw:dark:border-input tw:dark:bg-input/30 tw:dark:hover:bg-input/50',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] tw:aria-expanded:bg-secondary tw:aria-expanded:text-secondary-foreground',
        ghost:
          'tw:hover:bg-muted tw:hover:text-foreground tw:aria-expanded:bg-muted tw:aria-expanded:text-foreground tw:dark:hover:bg-muted/50',
        destructive:
          'tw:bg-destructive/10 tw:text-destructive tw:hover:bg-destructive/20 tw:focus-visible:border-destructive/40 tw:focus-visible:ring-destructive/20 tw:dark:bg-destructive/20 tw:dark:hover:bg-destructive/30 tw:dark:focus-visible:ring-destructive/40',
        link:
          'tw:text-primary tw:underline-offset-4 tw:hover:underline',
        // PORTAL CUSTOM VARIANTS
        success:
          'tw:border tw:border-green-700 tw:bg-transparent tw:text-green-700 tw:hover:bg-green-700 tw:hover:text-white',
        tab:
          'tw:bg-slate-200 tw:shadow-sm tw:hover:bg-accentdark/70 tw:hover:text-accent-foreground',
      },
      size: {
        default:
          'tw:h-9 tw:gap-1.5 tw:px-2.5 tw:in-data-[slot=button-group]:rounded-md tw:has-data-[icon=inline-end]:pr-2 tw:has-data-[icon=inline-start]:pl-2',
        xs:
          'tw:h-6 tw:gap-1 tw:rounded-[min(var(--radius-md),8px)] tw:px-2 tw:text-xs tw:in-data-[slot=button-group]:rounded-md tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:[&_svg:not([class*=size-])]:size-3',
        sm:
          'tw:h-8 tw:gap-1 tw:rounded-[min(var(--radius-md),10px)] tw:px-2.5 tw:in-data-[slot=button-group]:rounded-md tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5',
        lg:
          'tw:h-10 tw:gap-1.5 tw:px-2.5 tw:has-data-[icon=inline-end]:pr-2 tw:has-data-[icon=inline-start]:pl-2',
        icon:
          'tw:size-9',
        'icon-xs':
          'tw:size-6 tw:rounded-[min(var(--radius-md),8px)] tw:in-data-[slot=button-group]:rounded-md tw:[&_svg:not([class*=size-])]:size-3',
        'icon-sm':
          'tw:size-8 tw:rounded-[min(var(--radius-md),10px)] tw:in-data-[slot=button-group]:rounded-md',
        'icon-lg':
          'tw:size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'>
  & VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /**
     * The click is still in flight. Shows a spinner and stops the button being
     * pressed again, which is the whole point — every one of these sits in front
     * of a callable or a Firestore write, and a second press is a second one.
     *
     * Where the label already has a leading icon the spinner takes its place
     * rather than crowding in beside it, so the button neither grows nor reads
     * as having sprouted an extra glyph. Ignored under `asChild`, which must
     * render exactly the one child it was given.
     */
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';
  const isLoading = loading && !asChild;

  return (
    <Comp
      data-slot='button'
      data-variant={variant}
      data-size={size}
      aria-busy={isLoading || undefined}
      // `|| undefined` so `asChild` keeps rendering a bare <Link>: React warns
      // about a literal `disabled={false}` on an anchor.
      disabled={disabled || isLoading || undefined}
      className={cn(
        buttonVariants({ variant, size, className }),
        // The spinner stands in for whatever icon the label already had.
        isLoading && 'tw:[&>svg:not([data-slot=button-spinner])]:hidden',
      )}
      {...props}
    >
      {/* Passed straight through when not spinning: `Slot` counts its children
          and throws on anything but exactly one, so `asChild` must not see a
          stray `false` here. */}
      {isLoading ? (
        <>
          <Loader2 data-slot='button-spinner' className='tw:animate-spin' />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
